import React, { useState, useEffect } from 'react'
import { FormGroup, Label, Input } from 'reactstrap'
import Select from 'react-select'
import { buildWatchColumnOptions, getFieldLabel } from '../../fieldOptions'

const TriggerConfig = ({ nodeData, allAvailableModels, triggerTypes, onUpdate }) => {
    const [name, setName] = useState(nodeData.name || '')
    const [isActive, setIsActive] = useState(nodeData.isActive !== false)
    const [triggerType, setTriggerType] = useState(nodeData.triggerType || '')
    const [availableModel, setAvailableModel] = useState(nodeData.availableModel || null)
    const [columnsUpdated, setColumnsUpdated] = useState(nodeData.columnsUpdated || [])

    useEffect(() => {
        setName(nodeData.name || '')
        setIsActive(nodeData.isActive !== false)
        setTriggerType(nodeData.triggerType || '')
        setAvailableModel(nodeData.availableModel || null)
        setColumnsUpdated(nodeData.columnsUpdated || [])
    }, [nodeData])

    const handleChange = (updates) => {
        const merged = {
            name,
            isActive,
            triggerType,
            availableModel,
            columnsUpdated,
            ...updates,
        }
        onUpdate(merged)
    }

    const modelOptions = allAvailableModels?.edges?.map(({ node }) => ({
        label: `${node.contentType.appLabel.toUpperCase()} - ${node.contentType.model.toUpperCase()}`,
        value: node,
    })) || []

    const triggerTypeOptions = triggerTypes?.states?.map((t) => ({
        label: t.name.replaceAll('_', ' '),
        value: t.name,
    })) || []

    const currentModelValue = availableModel
        ? {
            label: `${availableModel.contentType?.appLabel?.toUpperCase() || ''} - ${availableModel.contentType?.model?.toUpperCase() || ''}`,
            value: availableModel,
        }
        : null

    const currentTriggerValue = triggerType
        ? { label: triggerType.replaceAll('_', ' '), value: triggerType }
        : null

    return (
        <div className="wf-config-form">
            <div className="wf-section">
                <div className="wf-section-label">General</div>
                <FormGroup>
                    <Label>Workflow Name</Label>
                    <Input
                        type="text"
                        value={name}
                        onChange={(e) => {
                            setName(e.target.value)
                            handleChange({ name: e.target.value })
                        }}
                        placeholder="e.g. New driver notification"
                    />
                </FormGroup>

                <div className="wf-toggle-row">
                    <span className="wf-toggle-label">Active</span>
                    <label className="wf-toggle">
                        <input
                            type="checkbox"
                            checked={isActive}
                            onChange={(e) => {
                                setIsActive(e.target.checked)
                                handleChange({ isActive: e.target.checked })
                            }}
                        />
                        <span className="wf-toggle-slider" />
                    </label>
                </div>
            </div>

            <div className="wf-section">
                <div className="wf-section-label">Trigger Setup</div>
                <FormGroup>
                    <Label>Model</Label>
                    <Select
                        className="bos-custom-select"
                        classNamePrefix="bos-select"
                        options={modelOptions}
                        value={currentModelValue}
                        placeholder="Select model..."
                        onChange={(opt) => {
                            setAvailableModel(opt.value)
                            handleChange({ availableModel: opt.value })
                        }}
                        menuPortalTarget={document.body}
                        styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                    />
                </FormGroup>

                <FormGroup>
                    <Label>Event</Label>
                    <Select
                        className="bos-custom-select"
                        classNamePrefix="bos-select"
                        options={triggerTypeOptions}
                        value={currentTriggerValue}
                        placeholder="Select trigger event..."
                        onChange={(opt) => {
                            setTriggerType(opt.value)
                            handleChange({ triggerType: opt.value })
                        }}
                        menuPortalTarget={document.body}
                        styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                    />
                </FormGroup>

                {triggerType === 'ON_UPDATE' && availableModel && (
                    <FormGroup>
                        <Label>Watch Columns</Label>
                        <Select
                            className="bos-custom-select"
                            classNamePrefix="bos-select"
                            isMulti
                            options={buildWatchColumnOptions(availableModel)}
                            value={columnsUpdated.map((c) => ({
                                label: getFieldLabel(availableModel, c),
                                value: c,
                            }))}
                            onChange={(opts) => {
                                const vals = (opts || []).map((o) => o.value)
                                setColumnsUpdated(vals)
                                handleChange({ columnsUpdated: vals })
                            }}
                            menuPortalTarget={document.body}
                            styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                        />
                    </FormGroup>
                )}
            </div>

            {availableModel && availableModel.recentObjects && (
                <div className="wf-section">
                    <div className="wf-section-label">
                        Sample Data
                        <span className="wf-section-label-badge">
                            {availableModel.contentType?.model}
                        </span>
                    </div>
                    <pre className="wf-preview-pre">
                        {(() => {
                            try {
                                const objs = JSON.parse(availableModel.recentObjects)
                                if (objs.length > 0) {
                                    return Object.entries(objs[0]).map(([k, v]) => (
                                        <div key={k}>
                                            <span className="wf-preview-key">{k}</span>
                                            <span className="wf-preview-sep">: </span>
                                            <span className="wf-preview-val">{JSON.stringify(v)}</span>
                                        </div>
                                    ))
                                }
                            } catch { /* ignore */ }
                            return <span className="text-muted">No recent objects</span>
                        })()}
                    </pre>
                </div>
            )}
        </div>
    )
}

export default TriggerConfig
